<template>
  <div class="view view_splash">
    <div class="view_splash__logo" />
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { mapState } from "pinia";
import * as SaveService from "@/api/SaveService";
import envVars from "@/helpers/Env";
import Compressor from "@/helpers/data/Compressor";
import { showError } from "@core/ui/alert/alert";

import { useUser } from "@/api/user.store";
import { useModels } from "@/api/models.store";
import { useProjectStore } from "@editor/store/project.store";
import type { Model } from "@/api/Model";

export default defineComponent({
  name: "SplashView",
  setup() {
    const { getOrLoadUser } = useUser();
    return {
      getOrLoadUser
    };
  },
  computed: {
    ...mapState(useProjectStore, ["isReadOnly"]),
    isTokenRequired() {
      return envVars.tokenRequired;
    }
  },
  created() {
    this.redirectToCorrectPage();
  },
  methods: {
    /**
     * Depending on environment, redirect to the correct page to proceed
     */
    async redirectToCorrectPage() {
      // load model if there's a given one
      if (envVars.fixedModel) {
        if (!envVars.isElectron || !window.electron?.getLoadedModel) {
          this.$router.push({ name: "softwareExpired" });
          return;
        }

        const rawModel = await window.electron.getLoadedModel();

        if (rawModel == null) {
          this.$router.push({ name: "softwareExpired" });
          return;
        }

        const model =
          typeof rawModel == "object" ? rawModel : JSON.parse(rawModel);

        await this.loadGivenModel(model);

        this.$router.push({
          name: "personal-workspace-model",
          params: {
            id: model.id
          }
        });

        return;
      }

      // otherwise, redirect to the login screen to get a token
      else {
        try {
          await this.getOrLoadUser({
            retryOnExpiredToken: false
          });
        } catch (error) {
          this.$router.push({
            name: "login"
          });
          return;
        }

        this.$router.replace({
          name: "models-explorer"
        });
      }
    },

    /**
     * Loads a model
     */
    async loadModelData(
      modelInfo: object,
      modelData: {
        data?: string | null;
        id?: number | string | null;
        media?: { uuid: string; data: Blob | string }[];
      }
    ) {
      // load the decrypted data
      await SaveService.loadInfoAndData(modelInfo, modelData);
    },

    /**
     * Load a model and lock the window to it.
     * TODO: Rework this.
     */
    async loadGivenModel(model: Model & { modelData: string }) {
      if (model == null) {
        showError(this.$t("errors.noGivenModelInserted"));
        return;
      }
      let name = model.name || "Modell";
      let data = model.modelData;

      // search for a saved version of this model in the local saves
      const { loadAllModels } = useModels();
      const saveList = await loadAllModels();
      const save = saveList.find((save) => {
        return save.name === name;
      });

      // load the saved version of the model if there's one
      if (save != null) {
        // TODO: Update this
        // const { updateLoadedModel } = useLoadedModelInfo();
        // return SaveService.loadLocal(save._id).then(() => {
        //   // set permissions for static offline
        //   updateLoadedModel({
        //     permissions: {
        //       canImport: false,
        //       canShare: false,
        //       canUpdate: true,
        //       canFork: false
        //     }
        //   });
        // });
      }

      // decompress the model data
      try {
        const decompressedData = Compressor.decompress(data);
        if (!decompressedData) return false;

        return this.loadModelData(
          {
            name: name,
            permissions: {
              canImport: false,
              canShare: false,
              canUpdate: true,
              canFork: false
            }
          },
          { data: decompressedData }
        );
      } catch (error) {
        showError(error as Error);
      }
    }
  }
});
</script>

<style lang="scss">
.view_splash {
  padding: 3rem;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  background-color: var(--color-background);

  &__logo {
    position: relative;
    max-width: 100%;
    width: 20rem;
    height: 10rem;
    background-image: url("~assets/logo.svg");
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center top;
    font-size: 1.5em;
    color: var(--color-action);
  }
}
</style>
